import React, { FC, useCallback, useMemo, useState } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { Box, Container, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link, Redirect, useHistory, useParams } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { CustomButton } from '../../components/UI/CustomButton';
import FullScreenBackgroundWrap from '../../components/UI/FullScreenBackgroudWrap';
import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { GET_CHARGEBEE_PLAN_INFO } from '../../graphql/prices';
import { MY_MEMBERSHIP_ROUTE } from '../../routes';
import { GetChargeBeePlanInfoArgs, LearnerInfo, Query } from '../../types';
import { getPriceVal } from '../../utils/helpers';
import { useAuth, usePaidSockets, useUpdateUserInfoAndToken } from '../../utils/hooks';
import SubscriptionForm from './components/SubscriptionForm';
import useStyles from './styles';

enum PaidType {
  Paid = 'paid',
  FreeTrial = 'free-trial',
}

declare global {
  interface Window {
    Chargebee: any;
  }
}

interface SubscriptionPageProps {
  logoUrl?: string;
  chargeBeeSite?: string;
  userInfo?: LearnerInfo;
}

const SubscriptionPage: FC<SubscriptionPageProps> = ({ logoUrl, chargeBeeSite, userInfo }) => {
  const classes = useStyles();

  const params = useParams();
  const history = useHistory();
  const planId = params?.id;

  const [loadingUser, setLoadingUser] = useState(false);

  const { t } = useTranslation();

  const isChargebeeLoaded = useScriptLoader(SCRIPTS.CHARGEBEE);

  const cbInstance = useMemo(() => {
    if (!isChargebeeLoaded) return;
    return window?.Chargebee?.init({
      site: chargeBeeSite,
    });
  }, [chargeBeeSite, isChargebeeLoaded]);

  const { data, loading } = useQuery<Pick<Query, 'getFilteredPricing'>, GetChargeBeePlanInfoArgs>(
    GET_CHARGEBEE_PLAN_INFO,
    { variables: { chargeBeePlanId: planId } },
  );
  const plan = data?.getFilteredPricing;
  const redirectUrl = `/payment-successful?type=${
    data?.getFilteredPricing?.free_trial_period?.count ? PaidType.Paid : PaidType.FreeTrial
  }`;

  const updateUserInfoCallback = useCallback(() => {
    setLoadingUser(false);
    history.replace(redirectUrl);
  }, [history, redirectUrl]);

  const { updateUserInfo } = useUpdateUserInfoAndToken(updateUserInfoCallback);

  const paidCallback = useCallback(() => {
    updateUserInfo(redirectUrl);
  }, [updateUserInfo, redirectUrl]);

  usePaidSockets(paidCallback, userInfo?.id);

  const { getAccessTokenSilently } = useAuth();

  const handleCheckout = useCallback(
    async (companyName?: string, subscriptionsAmount?: string) => {
      const data = new FormData();

      data.append('chargebee_plan_id', planId);
      if (companyName) {
        data.append('company', companyName);
      }
      if (subscriptionsAmount) {
        data.append('plan_quantity', subscriptionsAmount);
      }

      cbInstance.openCheckout({
        hostedPage: async () => {
          try {
            const token = await getAccessTokenSilently();
            const response = await fetch(
              `${process.env.REACT_APP_HTTP_API_URL}/api/chargebee/checkout-form`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'client-referer': window?.location?.hostname,
                },
                body: data,
              },
            );
            const res = await response.text();
            return JSON.parse(res);
          } catch (e) {}
        },
        success: () => {
          cbInstance.closeAll();
          setLoadingUser(true);
        },
      });
    },
    [planId, getAccessTokenSilently, cbInstance],
  );

  const maxUsers = plan?.chargebee_plan_info?.max_team_members || 0;
  const minUsers = plan?.chargebee_plan_info?.min_team_members || 0;

  const isIndividual =
    !plan?.enterprise &&
    ((!plan?.chargebee_plan_info?.max_team_members ??
      !plan?.chargebee_plan_info?.min_team_members) ||
      (plan?.chargebee_plan_info?.max_team_members === 1 &&
        plan?.chargebee_plan_info?.min_team_members === 1));
  let priceVal = getPriceVal(
    isIndividual && plan?.chargebee_plan_info?.is_taxable
      ? plan?.chargebee_plan_info?.price_with_tax
      : plan?.chargebee_plan_info?.price,
  );
  const price = priceVal.length ? Number(priceVal[0]) : '';
  const priceCurrency = priceVal.length > 1 ? priceVal[1] : 'GBP';

  const isIndividualSubscription = !((minUsers || minUsers === 0) && minUsers > 1);

  if (loadingUser) {
    return (
      <Loader>
        <Container className={classes.loaderTextWrapper}>
          <Typography variant="h3">{t('page.price.loaderText')}</Typography>
          <Typography variant="h3">{t('page.price.loaderText2')}</Typography>
          <br />
          <Typography variant="h3">{t('page.price.loaderText3')}</Typography>
        </Container>
      </Loader>
    );
  }

  if (userInfo?.has_subscription || userInfo?.owned_subscription) {
    return <Redirect to={MY_MEMBERSHIP_ROUTE} />;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <FullScreenBackgroundWrap logoUrl={logoUrl}>
      <>
        <Typography variant="h2" className={classes.title}>
          {t('page.subscription.subscribe')}
        </Typography>
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <Typography variant="h2" className={classes.title} align="center">
              {plan?.title}
            </Typography>
            <Box className={classes.cardUsers}>
              {plan?.enterprise
                ? `${maxUsers + 1}+`
                : isIndividualSubscription
                ? '1'
                : `${minUsers}-${maxUsers}`}{' '}
              {t(isIndividualSubscription ? 'common.user' : 'common.users')}
            </Box>
          </Box>
          <Box className={classes.innerCard}>
            {!plan?.enterprise && (
              <Box className={classes.priceBlock}>
                <Typography className={classes.priceAttr}>£</Typography>
                <Typography className={classes.price}>{price}</Typography>
                <Typography className={classes.priceAttr}>{priceCurrency}</Typography>
              </Box>
            )}
            <Typography className={classes.description}>{plan?.description}</Typography>
          </Box>
        </Box>
        <Link to="/pricing" className={classes.changePlan}>
          {t('page.subscription.changePlan')}
        </Link>
        {!isIndividualSubscription && (
          <SubscriptionForm
            maxUsers={maxUsers}
            minUsers={minUsers}
            handleCheckout={handleCheckout}
          />
        )}
        {isIndividualSubscription && (
          <CustomButton btnClass={classes.btn} onClick={() => handleCheckout()}>
            {t('button.subscribe')}
          </CustomButton>
        )}
      </>
    </FullScreenBackgroundWrap>
  );
};

export default SubscriptionPage;
